import { cn } from '@/lib/cn'

/**
 * Inline row actions, revealed on row hover.
 *
 * Prefer this over {@link TableRowActionsMenu} when a row has one to three
 * actions: a kebab menu costs two clicks and hides the verbs behind a glyph
 * that looks identical on every row. Reserve the menu for four or more actions,
 * or for actions whose labels won't fit as icons.
 *
 * The parent `<tr>` must carry `group/row`. Actions stay hidden until hover, but
 * are revealed whenever anything inside them takes focus (keyboard users never
 * tab into an invisible control) and are always visible on touch devices, which
 * have no hover state to trigger on.
 *
 * @example
 * <DataTableRow className="group/row">
 *   …
 *   <DataTableCell align="right">
 *     <TableRowActions>
 *       <TableRowAction icon={Pencil} label={`Edit ${name}`} onSelect={edit} />
 *       <TableRowAction icon={Trash2} label={`Delete ${name}`} variant="destructive" onSelect={remove} />
 *     </TableRowActions>
 *   </DataTableCell>
 * </DataTableRow>
 */
export interface TableRowActionsProps {
  children: React.ReactNode
  className?: string
}

export function TableRowActions({
  children,
  className,
}: TableRowActionsProps): React.ReactElement {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-0.5',
        'opacity-0 transition-opacity duration-150 motion-reduce:transition-none',
        'group-hover/row:opacity-100 group-focus-within/row:opacity-100',
        '[@media(hover:none)]:opacity-100',
        className
      )}
    >
      {children}
    </div>
  )
}

TableRowActions.displayName = 'TableRowActions'

export interface TableRowActionProps {
  icon: React.ComponentType<{ className?: string }>
  /** Full sentence used as the accessible name and the native tooltip, e.g. "Edit Salman Ahmad". */
  label: string
  onSelect: () => void
  variant?: 'default' | 'destructive'
  disabled?: boolean
}

export function TableRowAction({
  icon: Icon,
  label,
  onSelect,
  variant = 'default',
  disabled = false,
}: TableRowActionProps): React.ReactElement {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        // Rows are click-to-open; an action must not also trigger the row.
        event.stopPropagation()
        onSelect()
      }}
      className={cn(
        'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg',
        'transition-[background-color,color] duration-150 motion-reduce:transition-none',
        'focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40',
        'active:scale-[0.94] disabled:pointer-events-none disabled:opacity-40',
        variant === 'destructive'
          ? 'text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/15 dark:hover:text-red-400'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}

TableRowAction.displayName = 'TableRowAction'
